import { CheckCircle, XCircle } from 'lucide-react';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { OverviewSettingRow } from '@/components/agent/overview/OverviewSettingRow';
import { Badge } from '@/components/ui/badge';
import { getBrandForSubcategory } from '@/data/genies/brands';
import { getBrandForPersona } from '@/data/genies/persona-brands';
import { Agent, AgentConfig } from '@/types/agent/agent';
import type { PublicGeniePersona } from '@/types/discovery-catalog';
import { GenieBranding } from '@/types/ui/branding';

interface DemoGenieRowExpandedProps {
  persona: PublicGeniePersona;
  demoAgent: Agent | undefined;
}

const BRAND = "#4E9CFF";

const languageNames: Record<string, string> = {
  en: "English",
  es: "Spanish",
  fr: "French",
  de: "German",
  it: "Italian",
  pt: "Portuguese",
  nl: "Dutch",
  pl: "Polish",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
};

const formatSpeed = (speed: number) => {
  if (speed < 0.8) return `${speed}x (Slower)`;
  if (speed > 1.2) return `${speed}x (Faster)`;
  return `${speed}x (Normal)`;
};

const ProgressBar = ({ value, label }: { value: number; label: string }) => (
  <div className="flex items-center gap-3">
    <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${value * 100}%`, backgroundColor: BRAND }} />
    </div>
    <span className="font-medium text-xs">{label}</span>
  </div>
);

const StatusDot = ({ ok }: { ok: boolean }) =>
  ok ? <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-gray-300 shrink-0" />;

export const DemoGenieRowExpanded = ({ persona, demoAgent }: DemoGenieRowExpandedProps) => {
  const config = demoAgent?.settings as AgentConfig | undefined;
  const convConfig = config?.conversation_config;

  const speed = convConfig?.tts?.speed || 1;
  const stability = convConfig?.tts?.stability;
  const similarity = convConfig?.tts?.similarity_boost;
  const language = convConfig?.agent?.language || "en";
  const systemPrompt = convConfig?.agent?.prompt?.prompt;

  const capabilityLabels: Record<string, string> = {
    "emergency-response": "Emergency Response",
    scheduling: "Scheduling",
    quoting: "Quoting",
    troubleshooting: "Troubleshooting",
    concierge: "Concierge",
    "lead-qualification": "Lead Qualification",
    "follow-up": "Follow-Up",
    faq: "FAQ",
    "product-advisor": "Product Advisor",
    "order-tracking": "Order Tracking",
    intake: "Intake",
  };

  const registryBrand = getBrandForPersona(persona.id) || getBrandForSubcategory(persona.subcategoryId);
  const safeBrandName = registryBrand?.brandName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const logoPath = safeBrandName ? `/brand-logos/${safeBrandName}-stacked.png` : null;

  // Actual branding from the provisioned agent
  const agentBranding = demoAgent?.branding as Partial<GenieBranding> | undefined;
  const isProvisioned = !!demoAgent?.elevenlabs_id;

  return (
    <td colSpan={7} className="p-0 bg-gray-50 border-t-2 border-genie-primary">
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Identity & Purpose */}
          <OverviewSectionCard
            title="Identity & Purpose"
            subtitle="Core information about this genie"
            genieIcon="play"
            brand={BRAND}
          >
            <div className="space-y-4">
              <OverviewSettingRow label="Name" value={<span className="font-semibold">{persona.name}</span>} />
              <OverviewSettingRow label="Role" value={persona.role} />
              <OverviewSettingRow
                label="Industry"
                value={
                  <Badge className="bg-genie-primary/10 text-genie-primary border-genie-primary/20 border capitalize">
                    {persona.industryId}
                  </Badge>
                }
              />
              <OverviewSettingRow
                label="Subcategory"
                value={
                  <Badge variant="outline" className="capitalize">
                    {persona.subcategoryId}
                  </Badge>
                }
              />
              <OverviewSettingRow
                label="Capability"
                value={
                  <Badge className="bg-genie-accent-light text-genie-accent-dark border-genie-accent/20 border">
                    {capabilityLabels[persona.capabilityType] || persona.capabilityType}
                  </Badge>
                }
              />
              {persona.description && <OverviewSettingRow label="Description" value={persona.description} fullWidth />}
              {persona.config.firstMessage && (
                <OverviewSettingRow
                  label="Welcome Message"
                  value={
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm italic">"{persona.config.firstMessage}"</div>
                  }
                  fullWidth
                />
              )}
            </div>
          </OverviewSectionCard>

          {/* Branding - shows registry data + actual agent branding status */}
          {registryBrand && (
            <OverviewSectionCard
              title="Brand Identity"
              subtitle={`${registryBrand.brandName} — ${registryBrand.tagline}`}
              genieIcon="play"
              brand={registryBrand.primaryColor}
            >
              <div className="space-y-4">
                {/* Logo Preview */}
                <div className="flex items-start gap-4">
                  {/* Show agent's actual logo if provisioned, otherwise registry logo */}
                  <div className="w-24 h-24 rounded-lg border border-gray-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
                    {isProvisioned && agentBranding?.logoUrl ? (
                      <img
                        src={agentBranding.logoUrl}
                        alt={registryBrand.brandName}
                        className="w-full h-full object-contain p-1"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : logoPath ? (
                      <img
                        src={logoPath}
                        alt={registryBrand.brandName}
                        className="w-full h-full object-contain p-1"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <span className="text-xs text-genie-text-muted">No logo</span>
                    )}
                  </div>
                  <div className="space-y-2 flex-1">
                    <OverviewSettingRow label="Brand Name" value={<span className="font-semibold">{registryBrand.brandName}</span>} />
                    <OverviewSettingRow label="Tagline" value={registryBrand.tagline} />
                  </div>
                </div>
                {/* Colors - show actual agent branding colors if provisioned */}
                <OverviewSettingRow
                  label="Colors"
                  value={
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border border-gray-200"
                          style={{
                            backgroundColor:
                              isProvisioned && agentBranding?.primaryColor ? agentBranding.primaryColor : registryBrand.primaryColor,
                          }}
                        />
                        <span className="text-xs font-mono text-genie-text-muted">
                          {isProvisioned && agentBranding?.primaryColor ? agentBranding.primaryColor : registryBrand.primaryColor}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border border-gray-200"
                          style={{
                            backgroundColor:
                              isProvisioned && agentBranding?.secondaryColor ? agentBranding.secondaryColor : registryBrand.secondaryColor,
                          }}
                        />
                        <span className="text-xs font-mono text-genie-text-muted">
                          {isProvisioned && agentBranding?.secondaryColor ? agentBranding.secondaryColor : registryBrand.secondaryColor}
                        </span>
                      </div>
                    </div>
                  }
                />
                {/* Branding Status Checklist (when provisioned) */}
                {isProvisioned && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs font-medium text-genie-text-secondary mb-2">Agent Branding Status</p>
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs">
                        <StatusDot ok={!!agentBranding?.primaryColor} />
                        <span className={agentBranding?.primaryColor ? "text-genie-text-primary" : "text-genie-text-muted"}>
                          Primary Color
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <StatusDot ok={!!agentBranding?.secondaryColor} />
                        <span className={agentBranding?.secondaryColor ? "text-genie-text-primary" : "text-genie-text-muted"}>
                          Secondary Color
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <StatusDot ok={!!agentBranding?.logoUrl} />
                        <span className={agentBranding?.logoUrl ? "text-genie-text-primary" : "text-genie-text-muted"}>Logo Uploaded</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <StatusDot ok={!!agentBranding?.qrCodeStyle} />
                        <span className={agentBranding?.qrCodeStyle ? "text-genie-text-primary" : "text-genie-text-muted"}>
                          QR Code Style
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <StatusDot ok={!!agentBranding?.embedStyle} />
                        <span className={agentBranding?.embedStyle ? "text-genie-text-primary" : "text-genie-text-muted"}>Embed Style</span>
                      </div>
                    </div>
                  </div>
                )}
                {!isProvisioned && (
                  <OverviewSettingRow
                    label="Icon Hints"
                    value={<span className="text-xs text-genie-text-muted">{registryBrand.iconHints}</span>}
                  />
                )}
              </div>
            </OverviewSectionCard>
          )}

          {/* Voice & Speech Settings */}
          <OverviewSectionCard
            title="Voice & Speech Settings"
            subtitle={demoAgent?.elevenlabs_id ? "Live agent configuration" : "Will be configured on provision"}
            genieIcon="microphone"
            brand={BRAND}
          >
            <div className="space-y-4">
              {demoAgent?.elevenlabs_id ? (
                <>
                  <OverviewSettingRow
                    label="Voice"
                    value={<span className="font-medium">{demoAgent.voice_label || "Default Voice"}</span>}
                  />
                  <OverviewSettingRow
                    label="Language"
                    value={<span className="font-medium">{languageNames[language] || language.toUpperCase()}</span>}
                  />
                  <OverviewSettingRow label="Speech Speed" value={<ProgressBar value={(speed - 0.5) / 1.5} label={formatSpeed(speed)} />} />
                  {stability !== undefined && (
                    <OverviewSettingRow
                      label="Stability"
                      value={<ProgressBar value={stability} label={`${Math.round(stability * 100)}%`} />}
                    />
                  )}
                  {similarity !== undefined && (
                    <OverviewSettingRow
                      label="Similarity"
                      value={<ProgressBar value={similarity} label={`${Math.round(similarity * 100)}%`} />}
                    />
                  )}
                  <OverviewSettingRow
                    label="ElevenLabs ID"
                    value={
                      <span className="font-mono text-xs text-genie-text-muted truncate block max-w-[200px]">
                        {demoAgent.elevenlabs_id}
                      </span>
                    }
                  />
                </>
              ) : (
                <>
                  <div className="text-sm text-genie-text-muted mb-2">Voice settings will be configured when provisioned.</div>
                  <OverviewSettingRow label="Language" value={<span className="font-medium">English (Default)</span>} />
                  <OverviewSettingRow label="Speech Speed" value={<ProgressBar value={0.33} label="1x (Normal)" />} />
                  <OverviewSettingRow label="Stability" value={<ProgressBar value={0.5} label="50%" />} />
                  <OverviewSettingRow label="Similarity" value={<ProgressBar value={0.8} label="80%" />} />
                </>
              )}
            </div>
          </OverviewSectionCard>
        </div>

        {/* System Prompt Preview */}
        {systemPrompt && (
          <div className="mt-6">
            <OverviewSectionCard
              title="System Prompt"
              subtitle="Defines this genie's behavior and personality"
              genieIcon="play"
              brand={BRAND}
            >
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 max-h-40 overflow-y-auto whitespace-pre-wrap">
                {systemPrompt}
              </div>
            </OverviewSectionCard>
          </div>
        )}
      </div>
    </td>
  );
};
